import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, Grid2X2, ImageOff, Link2, List, Plus, Trash2, Upload, Wand2 } from 'lucide-react'
import { useOrganization } from '../contexts/OrganizationContext'
import { aiApi, mediaLibraryApi } from '../services/api'
import { BATCH_MEDIA_ASSET_LIMIT, runInChunks } from '../utils/batchRequests'
import {
  Alert,
  Badge,
  BoundedSplitPane,
  Button,
  CatalogGrid,
  Checkbox,
  ConfirmDialog,
  DataView,
  Dialog,
  Drawer,
  FilterBar,
  FormField,
  IconButton,
  Inspector,
  Menu,
  PageHeader,
  PageLayout,
  PageToolbar,
  SegmentedControl,
  Select,
  SelectionActionBar,
  Skeleton,
  StatePanel,
} from '../components/common'
import MediaSectionTabs from '../components/MediaSectionTabs'
import { assetModerationState } from '../components/moderation/assetModerationModel'
import { assetImageUrl } from '../utils/mediaVariants'
import { MEDIA_LIBRARY_SECTION, FEED_LIBRARY_EYEBROW } from '../config/studioRoutes'
import { ROLE_HIERARCHY, ROLES } from '../constants/roles'
import ImagePromptComposer from '../components/post-creator/ImagePromptComposer'
import PromptPresetNameDialog from '../components/post-creator/PromptPresetNameDialog'
import { canResumeImageGenerationJob, imageGenerationErrorMessage } from '../components/post-creator/imageGenerationJob'
import { useImageGenerationJob } from '../components/post-creator/useImageGenerationJob'
import { useReferenceImages } from '../components/post-creator/useReferenceImages'
import {
  MEDIA_UPLOAD_ACCEPT,
  mediaUploadContentType,
} from '../components/post-creator/mediaAttachments'
import { useMediaUploadQueue, hasActiveMediaUploads } from '../components/post-creator/useMediaUploadQueue'
import MediaUploadStatus from '../components/post-creator/MediaUploadStatus'
import { useClientWorkGuard } from '../components/post-creator/useClientWorkGuard'
import { useBlobPreviewUrlCleanup, usePendingMediaReplacement } from '../components/post-creator/composerShared'
import {
  AI_PROMPT_TEMPLATE_KIND,
  DEFAULT_AI_IMAGE_SIZE,
  EMPTY_AI_PROMPT_FIELDS,
  aiAssetTitle,
  aiPromptFallbackName,
  aiPromptFieldsFromTemplate,
  aiPromptTemplatePayload,
  aiPromptTextFromTemplate,
  buildImagePrompt,
  normalizeAiPromptFields,
} from '../components/post-creator/imagePrompt'
import styles from './MediaGalleryPage.module.css'

const UNDERSCORE_PATTERN = /_/g

const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Video' },
]


const EMPTY_URL_FORM = {
  title: '',
  asset_type: 'image',
  url: '',
}

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name', label: 'Name A–Z' },
]

const FILTER_DEFAULTS = {
  q: '',
  type: 'all',
  view: 'grid',
  sort: 'newest',
}

function enumParam(params, name, values, fallback) {
  const value = params.get(name)
  return values.includes(value) ? value : fallback
}

function safeDecode(value) {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function deriveAssetTitle(value, fallback = 'Asset') {
  if (!value) return fallback
  const normalizedValue = String(value)
  if (normalizedValue.startsWith('data:')) return fallback
  try {
    const url = new URL(normalizedValue)
    const lastSegment = url.pathname.split('/').filter(Boolean).at(-1)
    return safeDecode(lastSegment || fallback)
  } catch {
    const lastSegment = normalizedValue.split('?')[0].split('/').filter(Boolean).at(-1)
    return lastSegment ? safeDecode(lastSegment) : fallback
  }
}

// The asset's display name. The library's Name field edits the title, so it
// wins; file names and storage keys are fallbacks for untitled assets.
function assetFileName(asset, fallback = 'Untitled asset') {
  const title = typeof asset?.title === 'string' ? asset.title.trim() : ''
  return (
    title
    || asset?.metadata?.original_filename
    || asset?.metadata?.filename
    || deriveAssetTitle(asset?.object_key, '')
    || deriveAssetTitle(asset?.external_url, '')
    || deriveAssetTitle(asset?.url, '')
    || fallback
  )
}

function canonicalAssetKey(asset) {
  const objectKey = asset?.object_key || asset?.source_object_key
  if (objectKey) return objectKey
  const normalizedUrl = asset?.url ? String(asset.url).split('?')[0] : ''
  return normalizedUrl || asset?.id
}

function assetAssociatedPosts(asset) {
  const byId = new Map()
  const posts = asset?.associated_posts || []
  posts.forEach((post) => {
    if (post?.id) byId.set(post.id, post)
  })
  if (asset?.origin_post?.id && !byId.has(asset.origin_post.id)) {
    byId.set(asset.origin_post.id, asset.origin_post)
  }
  return Array.from(byId.values())
}

// The associated posts that keep an asset from being deleted: live posts
// (scheduled ones included) and posts waiting for approval. Archived,
// expired, rejected and pending-delete posts keep showing media they already
// use, so they do not block a delete. A post without in_use counts as in use.
function assetPostsInUse(asset) {
  return assetAssociatedPosts(asset).filter((post) => post?.in_use !== false)
}

function associatedPostStatus(post) {
  if (post?.lifecycle_label) return post.lifecycle_label
  const published = post?.is_published ? 'Published' : 'Not published'
  return post?.approval_status ? `${published} · ${post.approval_status.replace(UNDERSCORE_PATTERN, ' ')}` : published
}

const ASSET_IN_USE_MESSAGE = 'This media is used by live or pending posts. Archive those posts or remove the media from them first.'

// A media_asset_in_use refusal: the asset cannot be replaced or deleted while
// posts use it. Returns null for any other error.
function assetInUseConflict(error) {
  const data = error?.response?.data
  if (error?.response?.status !== 409 || data?.code !== 'media_asset_in_use') return null
  const posts = Array.isArray(data.referencing_posts) ? data.referencing_posts : []
  return {
    message: data.error || 'This media is used by posts.',
    postCount: Number.isFinite(data.post_count) ? data.post_count : posts.length,
    posts,
  }
}

function formatDateTime(value) {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

// Grid tiles load the w320 variant; the inspector, up to 400 CSS px wide,
// needs at least 800 px on a 2x screen.
const TILE_IMAGE_WIDTH = 320
const INSPECTOR_IMAGE_WIDTH = 800

function MediaPreview({ asset, alt, className, controls = false, imageWidth = TILE_IMAGE_WIDTH }) {
  const preferred = assetImageUrl(asset, imageWidth)
  const original = asset?.url
  const [failedSrc, setFailedSrc] = useState(null)

  useEffect(() => {
    setFailedSrc(null)
  }, [preferred])

  // A variant that fails to load falls back to the original before the
  // preview gives up.
  const src = failedSrc === null ? preferred : failedSrc !== original ? original : null
  const failed = failedSrc !== null && !src

  if (failed || !src) {
    return (
      <div className={`${className} ${styles.mediaFallback}`} role="img" aria-label={`Preview unavailable for ${alt}`}>
        <ImageOff size={24} aria-hidden="true" />
        <span>Preview unavailable</span>
      </div>
    )
  }

  if (asset.asset_type === 'video') {
    return <video src={src} muted playsInline controls={controls} className={className} onError={() => setFailedSrc(src)} />
  }

  return <img src={src} alt={alt} className={className} onError={() => setFailedSrc(src)} />
}

// A tile's bulk-select checkbox and quick delete. CatalogGrid renders them
// beside the option, so they never open the inspector.
function AssetTileActions({ asset, checked, onToggle, onQuickDelete }) {
  const title = assetFileName(asset)
  return (
    <>
      <div className={styles.assetSelect}>
        <Checkbox
          className={styles.assetSelectChoice}
          aria-label={`Select ${title}`}
          checked={checked}
          onChange={() => onToggle(asset.id)}
        />
      </div>
      <IconButton label={`Delete ${title}`} variant="danger" onClick={() => onQuickDelete(asset)}>
        <Trash2 size={16} aria-hidden="true" />
      </IconButton>
    </>
  )
}

function AssetTile({ asset, viewMode }) {
  const title = assetFileName(asset)
  const moderationState = assetModerationState(asset)
  return (
    <article className={`${styles.assetTile} ${viewMode === 'list' ? styles.assetTileList : ''}`} data-asset-id={asset.id}>
      <div className={styles.assetMedia}>
        <MediaPreview asset={asset} alt={title} className={styles.media} />
      </div>
      <div className={styles.assetBody}>
        <strong title={title}>{title}</strong>
        <span className={styles.assetMeta}>
          <span>{asset.asset_type === 'video' ? 'Video' : 'Image'}</span>
          {asset.status ? <Badge compact tone={asset.status === 'ready' ? 'success' : 'neutral'}>{asset.status.replace(UNDERSCORE_PATTERN, ' ')}</Badge> : null}
          {moderationState ? <Badge compact tone={moderationState.tone} title={moderationState.description}>{moderationState.label}</Badge> : null}
        </span>
      </div>
    </article>
  )
}

function MediaGalleryPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentOrg } = useOrganization()
  const queryClient = useQueryClient()
  const slug = currentOrg?.slug
  const canEditAssets = (ROLE_HIERARCHY[currentOrg?.role] || 0) >= ROLE_HIERARCHY[ROLES.EDITOR]
  // Studio sets an asset's workflow status; only Wager staff override it by
  // hand (the API refuses a change from anyone else).
  const canSetAssetStatus = (ROLE_HIERARCHY[currentOrg?.role] || 0) >= ROLE_HIERARCHY[ROLES.INTERNAL_ORG_ADMIN]

  const search = searchParams.get('q') || FILTER_DEFAULTS.q
  const typeFilter = enumParam(searchParams, 'type', TYPE_FILTERS.map(({ value }) => value), FILTER_DEFAULTS.type)
  const viewMode = enumParam(searchParams, 'view', ['grid', 'list'], FILTER_DEFAULTS.view)
  const sortOrder = enumParam(searchParams, 'sort', SORT_OPTIONS.map(({ value }) => value), FILTER_DEFAULTS.sort)
  const [selectedAssetId, setSelectedAssetId] = useState(null)
  const [mobileInspectorOpen, setMobileInspectorOpen] = useState(false)
  // Checked tiles for a bulk delete, independent of the inspector's single
  // selection. pendingDelete holds the ids a confirmation is asking about.
  const [checkedAssetIds, setCheckedAssetIds] = useState(() => new Set())
  const [pendingDelete, setPendingDelete] = useState(null)
  const [showUrlModal, setShowUrlModal] = useState(false)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [imagePrompt, setImagePrompt] = useState('')
  const [imagePromptFields, setImagePromptFields] = useState(EMPTY_AI_PROMPT_FIELDS)
  const [selectedPromptPresetId, setSelectedPromptPresetId] = useState('')
  const [referenceImages, setReferenceImages] = useState([])
  const [imageGenerationJob, setImageGenerationJob] = useState(null)
  const [promptPresetNameRequest, setPromptPresetNameRequest] = useState(null)
  const defaultPromptPresetAppliedRef = useRef(false)
  const uploadInputRef = useRef(null)
  const [pendingUploads, setPendingUploads] = useState([])
  const invalidateAssets = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['assets-page', slug] })
  }, [queryClient, slug])
  const mediaUploads = useMediaUploadQueue(setPendingUploads, {
    keepOnUnmount: true,
    onPersistedWhileUnmounted: invalidateAssets,
  })
  const handlePendingReplace = useCallback((id) => mediaUploads.dropPendingUpload(id), [mediaUploads])
  const { beginReplacement, consumeReplacement } = usePendingMediaReplacement(handlePendingReplace, uploadInputRef)
  useBlobPreviewUrlCleanup(pendingUploads)
  useClientWorkGuard(hasActiveMediaUploads(pendingUploads))
  const mobileInspectorTriggerRef = useRef(null)
  const [urlForm, setUrlForm] = useState(EMPTY_URL_FORM)
  const [assetForm, setAssetForm] = useState({
    title: '',
    status: 'ready',
    transcript_text: '',
  })

  const updateUrlState = (updates, { replace = false } = {}) => {
    const next = new URLSearchParams(searchParams)
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '' || value === FILTER_DEFAULTS[key]) next.delete(key)
      else next.set(key, value)
    })
    setSearchParams(next, { replace })
  }

  const clearFilters = () => {
    updateUrlState({ q: '', type: FILTER_DEFAULTS.type })
    setSelectedAssetId(null)
    setMobileInspectorOpen(false)
  }

  const assetsQuery = useInfiniteQuery({
    queryKey: ['assets-page', slug, search, typeFilter, sortOrder],
    queryFn: ({ pageParam = null }) => mediaLibraryApi.listAssets(slug, {
      ...(search ? { search } : {}),
      ...(typeFilter !== 'all' ? { type: typeFilter } : {}),
      sort: sortOrder,
      ...(pageParam ? { cursor: pageParam } : {}),
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.next_cursor ?? undefined,
    enabled: Boolean(slug),
  })

  const aiCapabilitiesQuery = useQuery({
    queryKey: ['assets-page', 'ai-capabilities', slug],
    queryFn: () => aiApi.getCapabilities(slug),
    enabled: Boolean(slug && canEditAssets),
  })

  const promptTemplatesQuery = useQuery({
    queryKey: ['assets-page', 'ai-prompt-presets', slug],
    queryFn: () => mediaLibraryApi.listTemplates(slug, { template_kind: AI_PROMPT_TEMPLATE_KIND }),
    enabled: Boolean(slug && canEditAssets),
    staleTime: 60_000,
  })

  const assets = useMemo(
    () => assetsQuery.data?.pages?.flatMap((page) => page?.items || []) || [],
    [assetsQuery.data?.pages],
  )
  const selectedAsset = assets.find((asset) => asset.id === selectedAssetId) || null
  const selectedAssetTitle = assetFileName(selectedAsset, 'Asset')
  const selectedAssetPosts = useMemo(() => assetAssociatedPosts(selectedAsset), [selectedAsset])
  const selectedModerationState = assetModerationState(selectedAsset)
  const aiCapabilities = aiCapabilitiesQuery.data || {}
  const imagePromptPresets = useMemo(
    () => (promptTemplatesQuery.data?.items || []).filter((template) => template.template_kind === AI_PROMPT_TEMPLATE_KIND && template.is_active !== false),
    [promptTemplatesQuery.data?.items],
  )
  const selectedPromptPreset = useMemo(
    () => imagePromptPresets.find((template) => template.id === selectedPromptPresetId) || null,
    [imagePromptPresets, selectedPromptPresetId],
  )

  useEffect(() => {
    if (defaultPromptPresetAppliedRef.current || !imagePromptPresets.length) return
    const defaultPreset = imagePromptPresets.find((preset) => preset.defaults?.ai_image_prompt?.is_default) || null
    if (!defaultPreset) return

    setSelectedPromptPresetId(defaultPreset.id)
    setImagePromptFields(aiPromptFieldsFromTemplate(defaultPreset))
    setImagePrompt(aiPromptTextFromTemplate(defaultPreset))
    defaultPromptPresetAppliedRef.current = true
  }, [imagePromptPresets])

  useEffect(() => {
    if (!selectedAsset) {
      setAssetForm({
        title: '',
        status: 'ready',
        transcript_text: '',
      })
      return
    }

    setAssetForm({
      title: assetFileName(selectedAsset, selectedAsset.title || ''),
      status: selectedAsset.status || 'ready',
      transcript_text: selectedAsset.transcript_text || '',
    })
  }, [selectedAsset])


  const createAssetMutation = useMutation({
    mutationFn: (payload) => mediaLibraryApi.createAsset(slug, payload),
    onSuccess: (createdAsset) => {
      toast.success('Asset saved')
      setUrlForm(EMPTY_URL_FORM)
      setShowUrlModal(false)
      invalidateAssets()
      if (createdAsset?.id) {
        setSelectedAssetId(createdAsset.id)
      }
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to save asset'),
  })


  // The latest media_asset_in_use refusal, keyed by the asset it is about so
  // it only renders in that asset's inspector.
  const [inUseConflict, setInUseConflict] = useState(null)

  useEffect(() => {
    setInUseConflict((current) => (current && current.assetId === selectedAssetId ? current : null))
  }, [selectedAssetId])

  // A new search, filter or sort is a new result set: a selection made in
  // the old one is not carried into it.
  useEffect(() => {
    setCheckedAssetIds(new Set())
  }, [slug, search, typeFilter, sortOrder])

  // Drops removed assets from the checked set and closes the inspector when
  // it shows one of them.
  const forgetAssets = (ids) => {
    if (!ids.length) return
    const removed = new Set(ids)
    setCheckedAssetIds((current) => new Set([...current].filter((id) => !removed.has(id))))
    if (selectedAssetId && removed.has(selectedAssetId)) {
      setSelectedAssetId(null)
      setMobileInspectorOpen(false)
    }
  }

  const updateAssetMutation = useMutation({
    mutationFn: (payload) => mediaLibraryApi.updateAsset(slug, selectedAsset.id, payload),
    onSuccess: () => {
      toast.success('Asset updated')
      setInUseConflict(null)
      invalidateAssets()
    },
    onError: (error) => {
      const conflict = assetInUseConflict(error)
      setInUseConflict(conflict ? { ...conflict, assetId: selectedAssetId } : null)
      toast.error(conflict?.message || error.response?.data?.error || 'Failed to update asset')
    },
  })

  const deleteAssetMutation = useMutation({
    mutationFn: (id) => mediaLibraryApi.deleteAsset(slug, id),
    onSuccess: (_, id) => {
      toast.success('Asset deleted')
      setInUseConflict(null)
      forgetAssets([id])
      invalidateAssets()
    },
    onError: (error, id) => {
      const conflict = assetInUseConflict(error)
      if (conflict) {
        // Show the refusal, with its post links, in this asset's inspector.
        setInUseConflict({ ...conflict, assetId: id })
        setSelectedAssetId(id)
      }
      toast.error(conflict?.message || 'Failed to delete asset')
    },
    onSettled: () => setPendingDelete(null),
  })

  const bulkDeleteAssetsMutation = useMutation({
    mutationFn: async (ids) => {
      const result = { requested: 0, deleted: [], missing: [], in_use: [], failed: [] }
      try {
        await runInChunks(ids, async (chunk) => {
          const response = await mediaLibraryApi.deleteAssets(slug, chunk)
          result.requested += response?.requested ?? chunk.length
          result.deleted.push(...(response?.deleted || []))
          result.missing.push(...(response?.missing || []))
          result.in_use.push(...(response?.in_use || []))
          result.failed.push(...(response?.failed || []))
        }, BATCH_MEDIA_ASSET_LIMIT)
      } catch (error) {
        // Chunks that finished before the failure stay deleted.
        throw Object.assign(error, { partialResult: result })
      }
      return result
    },
    onSuccess: (result) => {
      const removed = [...result.deleted, ...result.missing]
      forgetAssets(removed)
      if (removed.length === result.requested) {
        toast.success(removed.length === 1 ? 'Asset deleted' : `${removed.length} assets deleted`)
        return
      }
      const inUse = result.in_use.length
      const failed = result.failed.length
      toast.error(`Deleted ${removed.length} of ${result.requested} assets.${inUse ? ` ${inUse} used by live or pending posts.` : ''}${failed ? ` ${failed} failed.` : ''}`)
    },
    onError: (error, ids) => {
      const reason = error?.response?.data?.error || 'Failed to delete selected assets'
      const partial = error?.partialResult
      const removed = partial ? [...partial.deleted, ...partial.missing] : []
      forgetAssets(removed)
      // Earlier chunks are already gone for good; say so rather than only
      // reporting the failure that stopped the rest.
      toast.error(removed.length ? `Deleted ${removed.length} of ${ids.length} assets, then stopped: ${reason}` : reason)
    },
    onSettled: () => {
      setPendingDelete(null)
      invalidateAssets()
    },
  })

  const deletePending = deleteAssetMutation.isPending || bulkDeleteAssetsMutation.isPending

  const savePromptPresetMutation = useMutation({
    mutationFn: async ({ name, isDefault }) => {
      if (!slug) throw new Error('No organization selected')
      if (isDefault) {
        await Promise.all(
          imagePromptPresets
            .filter((preset) => preset.defaults?.ai_image_prompt?.is_default)
            .map((preset) => mediaLibraryApi.updateTemplate(slug, preset.id, {
              ...aiPromptTemplatePayload(preset.name, aiPromptFieldsFromTemplate(preset), aiPromptTextFromTemplate(preset), false),
              description: preset.description || null,
            }))
        )
      }
      return mediaLibraryApi.createTemplate(slug, aiPromptTemplatePayload(name, imagePromptFields, imagePrompt, isDefault))
    },
    onSuccess: (template) => {
      toast.success('AI prompt preset saved')
      setPromptPresetNameRequest(null)
      setSelectedPromptPresetId(template?.id || '')
      queryClient.invalidateQueries({ queryKey: ['assets-page', 'ai-prompt-presets', slug] })
    },
    onError: (error) => toast.error(error.response?.data?.error || error.message || 'Failed to save prompt preset'),
  })

  const uploadReferenceImage = useCallback(
    (file, fields) => mediaLibraryApi.uploadAsset(slug, file, fields),
    [slug],
  )
  const deleteReferenceImage = useCallback(
    (id) => mediaLibraryApi.deleteAsset(slug, id),
    [slug],
  )
  const referenceImageState = useReferenceImages({
    orgSlug: slug,
    value: referenceImages,
    onChange: setReferenceImages,
    uploadFile: uploadReferenceImage,
    deleteAsset: deleteReferenceImage,
  })

  // Generation runs through the AI job queue: a generation with reference
  // images can outlast the synchronous endpoint's provider timeout.
  const generateImageMutation = useImageGenerationJob({
    scope: slug,
    job: imageGenerationJob,
    setJob: setImageGenerationJob,
    buildInput: (size = DEFAULT_AI_IMAGE_SIZE) => {
      if (!slug) throw new Error('No organization selected')
      const promptFields = normalizeAiPromptFields(imagePromptFields)
      const prompt = imagePrompt.trim() || buildImagePrompt(promptFields)
      if (!prompt) throw new Error('Add prompt direction before generating an image')
      return {
        prompt,
        size,
        promptFields,
        presetId: selectedPromptPresetId || null,
        presetName: selectedPromptPreset?.name,
        referenceAssetIds: referenceImageState.items.filter((reference) => reference.status === 'ready').map((reference) => reference.id),
      }
    },
    createJob: ({ prompt, size, referenceAssetIds }) => aiApi.createImageJob(slug, {
      prompt,
      size,
      context_type: 'asset_library',
      ...(referenceAssetIds.length ? { reference_asset_ids: referenceAssetIds } : {}),
    }),
    getJob: (id) => aiApi.getJob(slug, id),
    complete: async (job, { prompt, size, promptFields, presetId, presetName, referenceAssetIds }) => {
      const url = job.result?.url
      if (!url) throw new Error('Image generation returned no asset URL')
      const objectKey = job.result?.object_key || null
      const asset = await mediaLibraryApi.createAsset(slug, {
        title: deriveAssetTitle(objectKey || url, aiAssetTitle(promptFields, aiPromptFallbackName(promptFields, prompt, presetName))),
        asset_type: 'image',
        source_kind: 'ai_generated',
        status: 'ready',
        url,
        object_key: objectKey,
        metadata: {
          ai: {
            prompt_fields: promptFields,
            compiled_prompt: prompt,
            size,
            preset_id: presetId,
            model: job.result?.model || null,
            job_id: job.id,
            parent_asset_id: null,
            versions: [],
            ...(referenceAssetIds.length ? { reference_asset_ids: referenceAssetIds } : {}),
          },
        },
        analysis: {},
      })
      return { asset }
    },
    onSuccess: ({ asset }) => {
      toast.success('Asset saved')
      invalidateAssets()
      if (asset?.id) setSelectedAssetId(asset.id)
      setImageGenerationJob(null)
      setShowGenerateModal(false)
    },
    onError: (error) => toast.error(imageGenerationErrorMessage(error)),
  })

  const filteredAssets = useMemo(() => {
    const seen = new Set()
    return assets.filter((asset) => {
      const key = canonicalAssetKey(asset)
      if (!key) return true
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [assets])

  const hasActiveFilters = Boolean(search || typeFilter !== FILTER_DEFAULTS.type)

  const checkedAssets = useMemo(
    () => filteredAssets.filter((asset) => checkedAssetIds.has(asset.id)),
    [filteredAssets, checkedAssetIds],
  )
  const inUseCheckedCount = checkedAssets.filter((asset) => assetPostsInUse(asset).length > 0).length
  const allLoadedChecked = filteredAssets.length > 0 && checkedAssets.length === filteredAssets.length
  const someLoadedChecked = checkedAssets.length > 0

  const toggleChecked = (id) => {
    setCheckedAssetIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleAllLoaded = () => {
    setCheckedAssetIds((current) => {
      const next = new Set(current)
      if (allLoadedChecked) filteredAssets.forEach((asset) => next.delete(asset.id))
      else filteredAssets.forEach((asset) => next.add(asset.id))
      return next
    })
  }

  // Asks to delete ids. An asset the list already shows as used by live or
  // pending posts is skipped up front (the server would refuse it); the
  // server still decides. Media only posts that are no longer live use can
  // go: those posts keep showing it.
  const requestDelete = (ids) => {
    const byId = new Map(assets.map((asset) => [asset.id, asset]))
    const deletable = []
    let skipped = 0
    let usedByPastPosts = 0
    ids.forEach((id) => {
      const asset = byId.get(id)
      if (assetPostsInUse(asset).length > 0) {
        skipped += 1
        return
      }
      deletable.push(id)
      if (assetAssociatedPosts(asset).length > 0) usedByPastPosts += 1
    })
    if (!deletable.length) {
      toast.error(ids.length === 1
        ? ASSET_IN_USE_MESSAGE
        : 'Every selected asset is used by live or pending posts. Archive those posts or remove the media from them first.')
      return
    }
    setPendingDelete({ ids: deletable, skipped, usedByPastPosts })
  }

  const quickDelete = (asset) => {
    // An in-use asset opens its inspector so its associated posts show.
    if (assetPostsInUse(asset).length > 0) setSelectedAssetId(asset.id)
    requestDelete([asset.id])
  }

  const confirmDelete = () => {
    if (!pendingDelete) return
    const { ids } = pendingDelete
    if (ids.length === 1) deleteAssetMutation.mutate(ids[0])
    else bulkDeleteAssetsMutation.mutate(ids)
  }

  const pendingDeleteCount = pendingDelete?.ids.length || 0
  const pendingDeleteSkipped = pendingDelete?.skipped || 0
  const pendingDeleteMessage = pendingDelete
    ? [
      pendingDelete.usedByPastPosts
        ? 'This removes the media from the library. It cannot be undone.'
        : 'This permanently removes the media from the library and from storage. It cannot be undone.',
      pendingDeleteSkipped
        ? `${pendingDeleteSkipped} selected ${pendingDeleteSkipped === 1 ? 'asset is' : 'assets are'} used by live or pending posts and will be skipped. Archive those posts or remove the media from them first.`
        : '',
    ].filter(Boolean).join(' ')
    : ''

  const handleImagePromptChange = (value) => {
    setImagePrompt(value)
    setSelectedPromptPresetId('')
  }

  const handleImagePromptFieldChange = (field, value) => {
    setImagePromptFields((currentValue) => ({
      ...currentValue,
      [field]: value,
    }))
    setSelectedPromptPresetId('')
  }

  const handleBuildPromptFromFields = () => {
    const compiledPrompt = buildImagePrompt(imagePromptFields)
    if (!compiledPrompt.trim()) {
      toast.error('Fill in at least one guided prompt field first')
      return
    }
    setImagePrompt(compiledPrompt)
  }

  const handlePromptPresetChange = (presetId) => {
    setSelectedPromptPresetId(presetId)
    if (!presetId) {
      setImagePromptFields(EMPTY_AI_PROMPT_FIELDS)
      setImagePrompt('')
      return
    }

    const preset = imagePromptPresets.find((item) => item.id === presetId)
    if (preset) {
      setImagePromptFields(aiPromptFieldsFromTemplate(preset))
      setImagePrompt(aiPromptTextFromTemplate(preset))
    }
  }

  const handleSavePromptPreset = (isDefault = false) => {
    if (!imagePrompt.trim() && !buildImagePrompt(imagePromptFields).trim()) {
      toast.error('Add a prompt or guided prompt details before saving')
      return
    }
    const fallbackName = aiPromptFallbackName(imagePromptFields, imagePrompt, selectedPromptPreset?.name)
    setPromptPresetNameRequest({ isDefault, initialName: fallbackName })
  }

  const confirmPromptPresetName = (name) => {
    if (!promptPresetNameRequest) return
    const { isDefault } = promptPresetNameRequest
    savePromptPresetMutation.mutate({ name, isDefault })
  }

  const openUrlForm = () => {
    setUrlForm(EMPTY_URL_FORM)
    setShowUrlModal(true)
  }

  const openGenerateForm = () => {
    setSelectedAssetId(null)
    // A previous failure belongs to that attempt, not to this one.
    if (!generateImageMutation.isPending && !canResumeImageGenerationJob(imageGenerationJob)) setImageGenerationJob(null)
    setShowGenerateModal(true)
  }

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length) {
      consumeReplacement(files)
      return
    }
    if (!slug) {
      consumeReplacement([])
      return
    }
    mediaUploads.enqueue(files, {
      upload: (file, mediaType, options) => mediaLibraryApi.uploadAsset(slug, file, {
        title: file.name,
        asset_type: mediaType,
        content_type: mediaUploadContentType(file),
        save_to_library: true,
      }, options),
      attach: (id, mediaType, file, asset) => {
        toast.success('Asset uploaded')
        invalidateAssets()
        if (asset?.id) setSelectedAssetId(asset.id)
        // The grid tile is the finished state; drop the placeholder card.
        mediaUploads.dropPendingUpload(id)
      },
      discard: (asset) => { mediaLibraryApi.deleteAsset(slug, asset.id).catch(() => {}) },
      // deliberately NO `analyze`: library uploads run no AI analysis
    })
    consumeReplacement(files)
  }

  const launchCreatePost = (asset) => {
    const assetForPost = asset ? { ...asset, title: assetFileName(asset) } : null
    navigate('/dashboard/feed/posts/new', asset ? {
      state: {
        prefills: {
          source: 'assets',
          template: {
            name: assetFileName(asset),
            defaults: {},
            assets: [assetForPost],
          },
        },
      },
    } : undefined)
  }

  const closeSelection = () => {
    const previousId = selectedAssetId
    setSelectedAssetId(null)
    setMobileInspectorOpen(false)
    window.requestAnimationFrame(() => {
      const card = [...document.querySelectorAll('[data-asset-id]')].find((node) => node.dataset.assetId === previousId)
      card?.closest('[role="option"]')?.focus()
    })
  }

  const addMediaMenu = (size = 'default') => (
    <Menu
      triggerLabel="Add media"
      trigger={(
        <Button size={size}>
          <Plus size={16} aria-hidden="true" /> Add media <ChevronDown size={16} aria-hidden="true" />
        </Button>
      )}
      items={[
        { key: 'upload', label: 'Upload file…', icon: Upload, onSelect: () => uploadInputRef.current?.click() },
        { key: 'url', label: 'Save by URL…', icon: Link2, onSelect: openUrlForm },
        { key: 'generate', label: 'Generate image…', icon: Wand2, onSelect: openGenerateForm },
      ]}
    />
  )

  const inspectorActions = selectedAsset && canEditAssets ? (
    <div className={styles.inspectorActions}>
      <Button onClick={() => launchCreatePost(selectedAsset)}>Use in post</Button>
      <Button
        variant="secondary"
        onClick={() => updateAssetMutation.mutate({
          title: assetForm.title,
          ...(canSetAssetStatus ? { status: assetForm.status } : {}),
          transcript_text: assetForm.transcript_text || null,
        })}
        loading={updateAssetMutation.isPending}
      >
        Save changes
      </Button>
      <Button
        variant="danger"
        onClick={() => requestDelete([selectedAsset.id])}
        disabled={deletePending}
      >
        Delete
      </Button>
    </div>
  ) : null

  const inspectorContent = selectedAsset ? (
    <div className={styles.inspectorContent}>
      <div className={styles.previewFrame}>
        <MediaPreview asset={selectedAsset} alt={selectedAssetTitle} controls imageWidth={INSPECTOR_IMAGE_WIDTH} className={styles.previewMedia} />
      </div>
      <div className={styles.inspectorMeta}>
        <Badge tone={selectedAsset.status === 'ready' ? 'success' : 'neutral'}>
          {(selectedAsset.status || 'unknown').replace(UNDERSCORE_PATTERN, ' ')}
        </Badge>
        {selectedModerationState ? <Badge tone={selectedModerationState.tone}>{selectedModerationState.label}</Badge> : null}
        <span>Updated {formatDateTime(selectedAsset.updated_at)}</span>
      </div>
      {selectedModerationState ? (
        <Alert variant={selectedModerationState.tone === 'danger' ? 'error' : selectedModerationState.tone === 'warning' ? 'warning' : 'info'}>
          {selectedModerationState.description}
        </Alert>
      ) : null}
      {inUseConflict ? (
        <Alert variant="warning" role="alert">
          <p>
            {inUseConflict.message}{' '}
            {inUseConflict.postCount === 1 ? '1 post uses it.' : `${inUseConflict.postCount} posts use it.`}
          </p>
          {inUseConflict.posts.length ? (
            <ul className={styles.associatedPostsList} aria-label="Posts using this media">
              {inUseConflict.posts.map((post) => (
                <li key={post.id}>
                  <button
                    type="button"
                    className={styles.associatedPost}
                    onClick={() => navigate(`/dashboard/feed/posts/${post.id}/edit`)}
                  >
                    <span>{post.text || 'Untitled post'}</span>
                    {post.lifecycle_label ? <Badge compact tone="neutral">{post.lifecycle_label}</Badge> : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </Alert>
      ) : null}
      <div className={styles.formStack}>
        <FormField
          label="Name"
          value={assetForm.title}
          disabled={!canEditAssets}
          onChange={(event) => setAssetForm((currentValue) => ({ ...currentValue, title: event.target.value }))}
        />
        <div className={styles.inlineFields}>
          <FormField label="Type" value={selectedAsset.asset_type === 'video' ? 'Video' : 'Image'} disabled />
          {canSetAssetStatus ? (
            <FormField
              label="Status"
              type="select"
              value={assetForm.status}
              onChange={(event) => setAssetForm((currentValue) => ({ ...currentValue, status: event.target.value }))}
              options={[
                { value: 'ready', label: 'Ready' },
                { value: 'processing', label: 'Processing' },
                { value: 'attached', label: 'Attached' },
                { value: 'in_feed', label: 'In feed' },
              ]}
            />
          ) : null}
        </div>
        <FormField label="URL" value={selectedAsset.url || 'Unavailable'} disabled />
        {selectedAsset.transcript_text || selectedAsset.asset_type === 'video' ? (
          <FormField
            label="Transcript"
            type="textarea"
            rows={5}
            value={assetForm.transcript_text}
            disabled={!canEditAssets}
            onChange={(event) => setAssetForm((currentValue) => ({ ...currentValue, transcript_text: event.target.value }))}
          />
        ) : null}
      </div>
      <section className={styles.associatedPostsSection} aria-label="Associated posts">
        <h3>Associated posts</h3>
        {selectedAssetPosts.length ? (
          <div className={styles.associatedPostsList}>
            {selectedAssetPosts.map((post) => canEditAssets ? (
              <button
                key={post.id}
                type="button"
                className={styles.associatedPost}
                onClick={() => navigate(`/dashboard/feed/posts/${post.id}/edit`)}
              >
                <span>{post.text || 'Untitled post'}</span>
                <small>{associatedPostStatus(post)}</small>
              </button>
            ) : (
              <div key={post.id} className={styles.associatedPostReadOnly}>
                <span>{post.text || 'Untitled post'}</span>
                <small>{associatedPostStatus(post)}</small>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.associatedPostsEmpty}>No associated posts</p>
        )}
      </section>
    </div>
  ) : null

  if (!slug) {
    return (
      <PageLayout>
        <StatePanel state="empty" title="Select an organization to open Media" />
      </PageLayout>
    )
  }

  const toolbar = (
    <PageToolbar
      label="Media controls"
      search={(
        <FilterBar
          query={search}
          onQueryChange={(value) => updateUrlState({ q: value }, { replace: true })}
          searchPlaceholder="Search media, file name, or post"
          label="Search media"
        />
      )}
      filters={(
        <div className={styles.primaryFilters}>
          <SegmentedControl
            label="Media type"
            items={TYPE_FILTERS}
            value={typeFilter}
            onValueChange={(value) => updateUrlState({ type: value })}
          />
          <Select
            className={styles.sortControl}
            size="compact"
            aria-label="Sort media"
            value={sortOrder}
            onChange={(event) => updateUrlState({ sort: event.target.value })}
          >
            {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </div>
      )}
      viewControls={(
        <div className={styles.viewToggle} aria-label="Media view">
          <IconButton label="Grid view" variant={viewMode === 'grid' ? 'secondary' : 'ghost'} aria-pressed={viewMode === 'grid'} onClick={() => updateUrlState({ view: 'grid' })}>
            <Grid2X2 size={16} aria-hidden="true" />
          </IconButton>
          <IconButton label="List view" variant={viewMode === 'list' ? 'secondary' : 'ghost'} aria-pressed={viewMode === 'list'} onClick={() => updateUrlState({ view: 'list' })}>
            <List size={16} aria-hidden="true" />
          </IconButton>
        </div>
      )}
    />
  )

  const catalogResults = (
    <DataView
      toolbar={toolbar}
      error={filteredAssets.length === 0 ? assetsQuery.error : null}
      items={assetsQuery.isLoading ? [{ id: 'loading' }] : filteredAssets}
      filtered={hasActiveFilters}
      onRetry={() => assetsQuery.refetch()}
      onClear={clearFilters}
      emptyTitle="No media in this library"
      emptyDescription="Add media to reuse it in future posts."
      searchEmptyTitle="No assets match your search or filters"
      primaryAction={canEditAssets ? addMediaMenu() : null}
      renderResults={() => assetsQuery.isLoading ? (
        <div className={styles.loadingCatalog}>
          <StatePanel state="loading" compact title="Loading media" description="Your media will appear here." />
          <div className={styles.skeletonGrid} aria-hidden="true">
            {Array.from({ length: 8 }, (_, index) => (
              <div key={index} className={styles.skeletonCard}>
                <Skeleton delay={150} height="9rem" label="Loading asset preview" />
                <Skeleton delay={150} shape="text" width="72%" label="Loading asset name" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className={styles.resultSummary}>
            <strong>{filteredAssets.length} {filteredAssets.length === 1 ? 'item' : 'items'}</strong>
            {canEditAssets && filteredAssets.length ? (
              <Checkbox
                className={styles.selectAllLoaded}
                label="Select all"
                aria-label="Select all loaded media"
                checked={allLoadedChecked}
                ref={(element) => {
                  if (element) element.indeterminate = someLoadedChecked && !allLoadedChecked
                }}
                onChange={toggleAllLoaded}
              />
            ) : null}
          </div>
          <CatalogGrid
            items={filteredAssets}
            selectedKeys={selectedAsset ? [selectedAsset.id] : []}
            onSelectionChange={([id]) => {
              setSelectedAssetId(id || null)
              setMobileInspectorOpen(false)
            }}
            columns={viewMode === 'list' ? 1 : selectedAsset ? 3 : 4}
            label="Media catalog"
            className={viewMode === 'list' ? styles.catalogList : ''}
            renderItem={(asset) => <AssetTile asset={asset} viewMode={viewMode} />}
            renderItemActions={canEditAssets ? (asset) => (
              <AssetTileActions
                asset={asset}
                checked={checkedAssetIds.has(asset.id)}
                onToggle={toggleChecked}
                onQuickDelete={quickDelete}
              />
            ) : undefined}
          />
          {assetsQuery.hasNextPage || assetsQuery.isFetchNextPageError || (assetsQuery.error && filteredAssets.length > 0) ? (
            <div className={styles.loadMoreContainer}>
              {assetsQuery.isFetchNextPageError || assetsQuery.error ? (
                <Button
                  variant="secondary"
                  onClick={() => assetsQuery.fetchNextPage()}
                >
                  Retry loading more
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => assetsQuery.fetchNextPage()}
                  loading={assetsQuery.isFetchingNextPage}
                  disabled={assetsQuery.isFetchingNextPage}
                >
                  Load more
                </Button>
              )}
            </div>
          ) : null}
        </>
      )}
    />
  )

  // Driven by the loaded, checked assets so an id that left the list never
  // counts or gets sent.
  const bulkActions = canEditAssets && checkedAssets.length > 0 ? (
    <SelectionActionBar
      count={checkedAssets.length}
      noun="asset"
      note={inUseCheckedCount > 0
        ? `${inUseCheckedCount} selected ${inUseCheckedCount === 1 ? 'asset is' : 'assets are'} used by live or pending posts and will be skipped.`
        : undefined}
      onClear={() => setCheckedAssetIds(new Set())}
      pending={deletePending}
    >
      <Button
        variant="danger"
        size="compact"
        onClick={() => requestDelete(checkedAssets.map((asset) => asset.id))}
        disabled={deletePending || inUseCheckedCount === checkedAssets.length}
      >
        <Trash2 size={14} aria-hidden="true" /> Delete selected
      </Button>
    </SelectionActionBar>
  ) : null

  return (
    <PageLayout size="workspace" className={selectedAsset ? styles.pageWithSelection : ''} stickyActions={bulkActions}>
      <PageHeader
        title={MEDIA_LIBRARY_SECTION.label}
        context={FEED_LIBRARY_EYEBROW}
        primaryAction={canEditAssets ? addMediaMenu('large') : null}
        secondaryActions={canEditAssets ? (
          <Button variant="secondary" onClick={() => launchCreatePost()}>
            <Plus size={16} aria-hidden="true" /> Create post
          </Button>
        ) : null}
      />
      <MediaSectionTabs />

      {pendingUploads.length > 0 ? (
        <div className={styles.uploadStatusList} aria-label="Media uploads">
          {pendingUploads.map((media) => (
            <MediaUploadStatus
              key={media.id}
              media={media}
              showAnalysisStage={false}
              onRetry={() => mediaUploads.retry(media.id)}
              onCancel={() => mediaUploads.cancel(media.id)}
              onRemove={() => mediaUploads.dropPendingUpload(media.id)}
              onReplace={() => {
                beginReplacement(media.id)
                uploadInputRef.current?.click()
              }}
            />
          ))}
        </div>
      ) : null}

      <input
        ref={uploadInputRef}
        className={styles.hiddenFileInput}
        type="file"
        accept={MEDIA_UPLOAD_ACCEPT}
        aria-label="Upload media"
        multiple
        tabIndex={-1}
        disabled={!canEditAssets}
        onChange={handleFileUpload}
      />

      {selectedAsset ? (
        <BoundedSplitPane
          inspectorWidth="22.5rem"
          inspectorLabel="Selected asset"
          primary={catalogResults}
          inspector={(
            <Inspector title={selectedAssetTitle} onClose={closeSelection} footer={inspectorActions}>
              {inspectorContent}
            </Inspector>
          )}
        />
      ) : catalogResults}

      {/* The bulk bar takes the bottom slot; the Details bar returns when the selection is cleared. */}
      {selectedAsset && !bulkActions ? (
        <div className={styles.mobileSelectionBar} role="status">
          <div className={styles.selectionSummary}>
            <strong>1 selected</strong>
            <span>{selectedAssetTitle}</span>
          </div>
          <Button ref={mobileInspectorTriggerRef} variant="secondary" onClick={() => setMobileInspectorOpen(true)}>Details</Button>
          {canEditAssets ? <Button onClick={() => launchCreatePost(selectedAsset)}>Use in post</Button> : null}
        </div>
      ) : null}

      <Drawer
        open={Boolean(selectedAsset && mobileInspectorOpen)}
        title={selectedAssetTitle}
        side="right"
        onClose={() => setMobileInspectorOpen(false)}
        returnFocusRef={mobileInspectorTriggerRef}
        footer={inspectorActions}
      >
        {inspectorContent}
      </Drawer>

      <Dialog
        open={showUrlModal}
        title="Save by URL"
        description="Add an externally hosted image or video to this organization’s library."
        onClose={() => setShowUrlModal(false)}
        footer={(
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setShowUrlModal(false)}>Cancel</Button>
            <Button
              loading={createAssetMutation.isPending}
              onClick={() => createAssetMutation.mutate({
                title: urlForm.title || deriveAssetTitle(urlForm.url),
                asset_type: urlForm.asset_type,
                source_kind: 'external_url',
                status: 'ready',
                url: urlForm.url,
                external_url: urlForm.url,
                metadata: {},
                analysis: {},
              })}
              disabled={!urlForm.url.trim()}
            >
              Save
            </Button>
          </div>
        )}
      >
        <div className={styles.formStack}>
          <FormField label="Name" value={urlForm.title} onChange={(event) => setUrlForm((value) => ({ ...value, title: event.target.value }))} placeholder="Optional" />
          <FormField
            label="Type"
            type="select"
            value={urlForm.asset_type}
            onChange={(event) => setUrlForm((value) => ({ ...value, asset_type: event.target.value }))}
            options={[{ value: 'image', label: 'Image' }, { value: 'video', label: 'Video' }]}
          />
          <FormField label="Source URL" value={urlForm.url} onChange={(event) => setUrlForm((value) => ({ ...value, url: event.target.value }))} />
        </div>
      </Dialog>

      <Dialog open={showGenerateModal} title="Generate image" size="large" onClose={() => setShowGenerateModal(false)}>
        <ImagePromptComposer
          imagePrompt={imagePrompt}
          imagePromptFields={imagePromptFields}
          onImagePromptChange={handleImagePromptChange}
          onImagePromptFieldChange={handleImagePromptFieldChange}
          onBuildPromptFromFields={handleBuildPromptFromFields}
          imagePromptPresets={imagePromptPresets}
          selectedPromptPresetId={selectedPromptPresetId}
          onPromptPresetChange={handlePromptPresetChange}
          onSavePromptPreset={() => handleSavePromptPreset(false)}
          onSaveDefaultPromptPreset={() => handleSavePromptPreset(true)}
          promptPresetSaving={savePromptPresetMutation.isPending}
          imagePending={generateImageMutation.isPending}
          imageStatus={imageGenerationJob}
          onGenerateImage={(imageSize) => generateImageMutation.mutate(imageSize)}
          onDiscardImage={generateImageMutation.discardJob}
          aiCapabilities={aiCapabilities}
          referenceImages={referenceImageState.items}
          onAddReferenceImages={referenceImageState.addFiles}
          onRemoveReferenceImage={referenceImageState.removeReference}
          referenceImagesUploading={referenceImageState.uploading}
          generateLabel="Generate"
          framed={false}
        />
      </Dialog>

      {promptPresetNameRequest ? (
        <PromptPresetNameDialog
          open
          isDefault={promptPresetNameRequest.isDefault}
          initialName={promptPresetNameRequest.initialName}
          saving={savePromptPresetMutation.isPending}
          onConfirm={confirmPromptPresetName}
          onCancel={() => setPromptPresetNameRequest(null)}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={pendingDeleteCount > 1 ? `Delete ${pendingDeleteCount} assets?` : 'Delete asset?'}
        message={pendingDeleteMessage}
        variant="danger"
        confirmLabel={pendingDeleteCount > 1 ? `Delete ${pendingDeleteCount} assets` : 'Delete asset'}
        loading={deletePending}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deletePending) setPendingDelete(null) }}
      />
    </PageLayout>
  )
}

export default MediaGalleryPage
